import { yupResolver } from '@hookform/resolvers/yup';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Container,
  Divider,
  FormHelperText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { AppSkeleton } from 'src/components/common/atoms/AppSkeleton';
import { SearchConditionLabel } from 'src/components/kanri/atoms/SearchConditionLabel';
import { SearchConditionTextField } from 'src/components/kanri/atoms/SearchConditionTextField';
import { SelectableChip } from 'src/components/kanri/atoms/SelectableChip';
import { appConst, RoleId } from 'src/constants/common';
import { useNavigateSystemError } from 'src/hooks/common/useNavigateSystemError';
import { useUserData } from 'src/hooks/common/useUserData';
import { Soshiki } from 'src/types/common/Soshiki';
import { SpCheck } from 'src/types/common/SpCheck';
import { ConditionType } from 'src/types/kanri/ConditionType';
import { SearchCallerType } from 'src/types/kanri/SearchCallerType';
import { getSpCheck } from 'src/utilities/restApi/common/spCheck';
import { getSoshikiList } from 'src/utilities/restApi/kanri/soshiki';
import * as yup from 'yup';
import { SearchConditions } from '../types/SupportersSearchConditions';

const SEARCH_CONDITION_LABEL_WIDTH = 70;

type ComponentProps = {
  isLoading?: SearchCallerType;
  searchButtonOnClick: (searchConditions: SearchConditions) => void;
};

type FormValues = {
  spFName?: string | null;
  spSName?: string | null;
  spSoshiki?: Soshiki | null;
};

export const SearchConditionField = ({ isLoading, searchButtonOnClick }: ComponentProps) => {
  const navigateSystemError = useNavigateSystemError();

  const { userData } = useUserData();
  const [expanded, setExpanded] = useState<boolean>(false);
  const [searchType, setSearchType] = useState<ConditionType>('simple');

  // view data
  const [spCheckList, setSpCheckList] = useState<SpCheck[] | undefined>(undefined);
  const [soshikiList, setSoshikiList] = useState<Soshiki[] | undefined>(undefined);

  // simple search
  const [selectedSpCheckId, setSelectedSpCheckId] = useState<string>(appConst.STR_EMPTY);
  const [selectedSoshikiList, setSelectedSoshikiList] = useState<Soshiki[]>([]);

  useEffect(() => {
    if (userData) {
      getSpCheck()
        .then((response) => {
          if (response.data) {
            const newData = response.data.spcheck_list.map((x) => {
              return {
                spCheckId: x.spcheckid,
                spCheckStatus: x.spcheckstatus,
              } as SpCheck;
            });
            setSpCheckList(newData);
          }
        })
        .catch(() => navigateSystemError());
    }

    getSoshikiList()
      .then((response) => {
        if (response.data) {
          const newData = response.data.soshiki_list.map((x) => {
            return {
              soshikiId: x.soshikiid,
              soshikiName: x.soshikiname,
            } as Soshiki;
          });
          setSoshikiList(newData);
        }
      })
      .catch(() => navigateSystemError());
  }, [userData]);

  // validation
  const schema: yup.ObjectSchema<FormValues> = yup.object(
    searchType === 'advanced'
      ? {
          spFName: yup
            .string()
            .test('spFNameRequired1', '支援者名は必須です。', () => {
              let isValid = true;
              const { spFName, spSName, spSoshiki } = getValues();
              if (!spFName && !spSName && !spSoshiki) {
                isValid = false;
              }
              return isValid;
            })
            .test('spFNameRequired2', '姓または名のいずれかは必須です。', (value) => {
              let isValid = true;
              const { spSName, spSoshiki } = getValues();
              if (spSoshiki) {
                if (!spSName && !value) {
                  isValid = false;
                }
              }
              return isValid;
            })
            .max(200),
          spSName: yup
            .string()
            .test('spFName', '姓または名のいずれかは必須です。', (value) => {
              let isValid = true;
              const { spFName, spSoshiki } = getValues();
              if (spSoshiki) {
                if (!spFName && !value) {
                  isValid = false;
                }
              }
              return isValid;
            })
            .max(200),
          spSoshiki:
            userData?.roleId === RoleId.JichitaiStaff
              ? yup
                  .mixed<Soshiki>()
                  .nullable()
                  .test('spSoshikiRequired', '所属は必須です。', (value) => {
                    let isValid = true;
                    const { spFName, spSName } = getValues();
                    if (!value) {
                      if (spFName || spSName) {
                        isValid = false;
                      }
                    }
                    return isValid;
                  })
              : yup.mixed<Soshiki>().notRequired(),
        }
      : {
          spFName: yup.string().notRequired(),
          spSName: yup.string().notRequired(),
          spSoshiki: yup.mixed<Soshiki>().notRequired(),
        }
  );

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    getValues,
    trigger,
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      spFName: '',
      spSName: '',
      spSoshiki: null,
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = handleSubmit((data) => {
    setExpanded(false);
    const searchConditions: SearchConditions = {
      simple:
        searchType === 'simple'
          ? {
              spCheckId: selectedSpCheckId,
              soshikiList: selectedSoshikiList,
              prsid_list: undefined,
            }
          : undefined,
      advanced:
        searchType === 'advanced'
          ? {
              spFName: data.spFName ?? undefined,
              spSName: data.spSName ?? undefined,
              spSoshiki: data.spSoshiki ?? undefined,
            }
          : undefined,
    };
    searchButtonOnClick(searchConditions);
  });

  const handleSearchType = (_event: React.MouseEvent<HTMLElement>, newSearchType: ConditionType | null) => {
    if (newSearchType) {
      setSearchType(newSearchType);
    }
  };

  const clearButtonOnClick = () => {
    // 検索ボタンクリック
    reset();
    setSearchType('simple');
    setSelectedSpCheckId(appConst.STR_EMPTY);
    setSelectedSoshikiList([]);
  };

  return (
    <form onSubmit={onSubmit}>
      <div>
        <Accordion
          variant="outlined"
          disableGutters
          expanded={expanded}
          onChange={(_event: React.SyntheticEvent, expanded: boolean) => setExpanded(expanded)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>検索条件</AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <ToggleButtonGroup size="small" color="primary" value={searchType} exclusive onChange={handleSearchType}>
                <ToggleButton value="simple">シンプルな検索</ToggleButton>
                <ToggleButton value="advanced">氏名検索</ToggleButton>
              </ToggleButtonGroup>
              <Stack spacing={1} width="100%">
                {searchType === 'simple' ? (
                  <>
                    {/* 支援可否 */}
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'start', sm: 'center' }}
                    >
                      <SearchConditionLabel width={SEARCH_CONDITION_LABEL_WIDTH}>支援可否</SearchConditionLabel>
                      <Grid container spacing={1}>
                        <Grid>
                          <SelectableChip
                            label="すべて"
                            selected={selectedSpCheckId === appConst.STR_EMPTY}
                            onClick={() => setSelectedSpCheckId(appConst.STR_EMPTY)}
                          />
                        </Grid>
                        <Divider orientation="vertical" flexItem sx={{ marginX: 1 }} />
                        {spCheckList ? (
                          spCheckList.map((x, i) => (
                            <Grid key={`spCheck-${i}`}>
                              <SelectableChip
                                label={x.spCheckStatus}
                                selected={selectedSpCheckId === x.spCheckId}
                                onClick={() => setSelectedSpCheckId(x.spCheckId)}
                              />
                            </Grid>
                          ))
                        ) : (
                          <AppSkeleton width={300} />
                        )}
                      </Grid>
                    </Stack>
                    {/* 所属 */}
                    {userData?.roleId === RoleId.JichitaiStaff && (
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'start', sm: 'center' }}
                      >
                        <SearchConditionLabel width={SEARCH_CONDITION_LABEL_WIDTH}>所属</SearchConditionLabel>
                        {soshikiList ? (
                          <Autocomplete
                            multiple
                            options={soshikiList}
                            value={selectedSoshikiList}
                            onChange={(_event, newValue) => {
                              setSelectedSoshikiList(newValue);
                            }}
                            getOptionLabel={(option) => option.soshikiName}
                            filterSelectedOptions
                            isOptionEqualToValue={(option, value) => option.soshikiId === value.soshikiId}
                            renderInput={(params) => <TextField {...params} size="small" />}
                            size="small"
                            ChipProps={{ color: 'primary' }}
                            sx={{ width: { xs: '100%', sm: 500 } }}
                          />
                        ) : (
                          <AppSkeleton width={300} />
                        )}
                      </Stack>
                    )}
                  </>
                ) : (
                  <>
                    <FormHelperText>氏名検索は検索に時間がかかる場合があります</FormHelperText>
                    <Stack direction="column" spacing={1}>
                      <Stack direction="row" alignItems="center">
                        <SearchConditionLabel width={SEARCH_CONDITION_LABEL_WIDTH}>支援者名</SearchConditionLabel>
                        {userData?.roleId === RoleId.JichitaiStaff && (
                          <Typography variant="caption">（※所属の選択は必須です。）</Typography>
                        )}
                      </Stack>
                      {soshikiList ? (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ '& > :not(style)': { m: 1 } }}>
                          <Container maxWidth="md">
                            <Grid container spacing={1} sx={{ width: '100%' }}>
                              <Grid xs={12} md={4}>
                                <Controller
                                  name="spFName"
                                  control={control}
                                  render={({ field }) => (
                                    <SearchConditionTextField
                                      {...field}
                                      label="姓"
                                      autoComplete="off"
                                      onChange={(e) => {
                                        setValue('spFName', e.target.value);
                                        trigger(['spFName', 'spSName', 'spSoshiki']);
                                      }}
                                      error={'spFName' in errors}
                                      helperText={errors.spFName?.message ?? appConst.LABEL_HALF_SPACE}
                                      fullWidth
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid xs={12} md={4}>
                                <Controller
                                  name="spSName"
                                  control={control}
                                  render={({ field }) => (
                                    <SearchConditionTextField
                                      {...field}
                                      label="名"
                                      autoComplete="off"
                                      onChange={(e) => {
                                        setValue('spSName', e.target.value);
                                        trigger(['spFName', 'spSName', 'spSoshiki']);
                                      }}
                                      error={'spSName' in errors}
                                      helperText={errors.spSName?.message ?? appConst.LABEL_HALF_SPACE}
                                      fullWidth
                                    />
                                  )}
                                />
                              </Grid>
                              {userData?.roleId === RoleId.JichitaiStaff && (
                                <Grid xs={12} md={4}>
                                  <Controller
                                    name="spSoshiki"
                                    control={control}
                                    render={({ field }) => {
                                      return (
                                        <Autocomplete
                                          {...field}
                                          disablePortal
                                          options={soshikiList}
                                          getOptionLabel={(option) => option.soshikiName}
                                          size="small"
                                          onChange={(_event, newValue) => {
                                            setValue('spSoshiki', newValue);
                                            trigger(['spFName', 'spSName', 'spSoshiki']);
                                          }}
                                          isOptionEqualToValue={(option, value) => option.soshikiId === value.soshikiId}
                                          renderInput={(params) => (
                                            <SearchConditionTextField
                                              {...params}
                                              label="所属"
                                              autoComplete="off"
                                              error={'spSoshiki' in errors}
                                              helperText={errors.spSoshiki?.message ?? appConst.LABEL_HALF_SPACE}
                                              fullWidth
                                            />
                                          )}
                                        />
                                      );
                                    }}
                                  />
                                </Grid>
                              )}
                            </Grid>
                          </Container>
                        </Stack>
                      ) : (
                        <AppSkeleton width={300} />
                      )}
                    </Stack>
                  </>
                )}
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="center" alignItems="center" spacing={1}>
                <Button
                  variant="contained"
                  sx={{ width: { xs: '100%', sm: '300px' } }}
                  type="submit"
                  disabled={isLoading === 'user'}
                >
                  検索
                </Button>
                <Button onClick={() => clearButtonOnClick()}>条件をクリア</Button>
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </div>
    </form>
  );
};
